import React, { useState } from 'react';
import Cropper from 'react-easy-crop';
import {
  Alert,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import {
  GALLERY_RATIO_VALUE,
  adaptCropToRatio,
} from '../LandingPageEditor/landingComponents';
import { MIN_RECOMMENDED_WIDTH } from './useLandingAssetUpload';

const CROPPER_STYLE = {
  containerStyle: {
    position: 'relative',
    height: '360px',
    background: '#0b141f',
    borderRadius: '4px',
  },
};

const MIN_ZOOM = 1;
const DEFAULT_MAX_ZOOM = 4;

const round = (value) => Math.round(value * 10000) / 10000;

// A zoom minimo l'inquadratura è già la più larga che quel rapporto consente
// dentro l'immagine: suggerire di ridurre lo zoom sarebbe un consiglio che
// l'operatore non può seguire, e il file resta l'unica cosa da cambiare.
const lowResMessage = (canZoomOut) =>
  canZoomOut
    ? `This crop is under ${MIN_RECOMMENDED_WIDTH}px wide and will look soft on phone screens. Zoom out or upload a larger image.`
    : `This image is under ${MIN_RECOMMENDED_WIDTH}px wide even at full frame, so it will look soft on phone screens whatever you crop. Upload a larger image.`;

// react-easy-crop dà l'area in percentuali 0-100; il dato salvato usa frazioni
// 0-1, la stessa scala con cui il runtime costruisce le variabili CSS.
const toCrop = (area, media, ratio) => ({
  x: round(area.x / 100),
  y: round(area.y / 100),
  w: round(area.width / 100),
  h: round(area.height / 100),
  ratio,
  iw: media.naturalWidth,
  ih: media.naturalHeight,
});

const GalleryCropModal = ({ image, aspectRatio, onSave, onRemove, onClose }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [media, setMedia] = useState(null);
  const [area, setArea] = useState(null);

  // Calcolato una volta sola: react-easy-crop lo legge al caricamento
  // dell'immagine, e ricalcolarlo a ogni render rimbalzerebbe l'inquadratura
  // mentre l'operatore trascina.
  //
  // Senza ritaglio si lascia undefined: a zoom 1 il cropper inquadra da sé il
  // rettangolo più grande della proporzione richiesta che entra
  // nell'immagine, cioè esattamente la resa `cover`. Un'immagine mai
  // ritagliata si apre quindi già sul comportamento di default.
  const [initialArea] = useState(() => {
    const derived = adaptCropToRatio(image.crop, aspectRatio);
    if (!derived) return undefined;
    return {
      x: derived.x * 100,
      y: derived.y * 100,
      width: derived.w * 100,
      height: derived.h * 100,
    };
  });

  // react-easy-crop deriva lo zoom necessario a ripristinare
  // initialCroppedAreaPercentages dalla sola larghezza: zoom = cropSize.width
  // / mediaSize.width * (100 / area.width). Quando il rapporto si allarga
  // rispetto a quello con cui il ritaglio è stato salvato, adaptCropToRatio
  // mantiene la larghezza in pixel ma il rettangolo a zoom 1 cresce, quindi lo
  // zoom richiesto sale in proporzione e può superare una costante fissa,
  // lasciando l'inquadratura pubblicata irraggiungibile con lo slider. Va
  // ricalcolato per ogni immagine/rapporto — non va "semplificato" a 4.
  const [maxZoom] = useState(() => {
    const derived = adaptCropToRatio(image.crop, aspectRatio);
    const ratioValue = GALLERY_RATIO_VALUE[aspectRatio];
    if (!derived || !ratioValue) return DEFAULT_MAX_ZOOM;
    const zoomOneWidth = Math.min(derived.iw, derived.ih * ratioValue) / derived.iw;
    return Math.max(
      DEFAULT_MAX_ZOOM,
      Math.ceil((zoomOneWidth / derived.w) * 100) / 100,
    );
  });

  const value = area && media ? toCrop(area, media, aspectRatio) : null;
  // Stesso avviso dell'upload, stessa soglia: è il problema "immagine sgranata"
  // che gli operatori già conoscono, causato dallo zoom invece che dal file.
  const lowRes = Boolean(value) && value.w * value.iw < MIN_RECOMMENDED_WIDTH;

  return (
    <Modal isOpen size="lg" toggle={onClose}>
      <ModalHeader toggle={onClose}>Crop image</ModalHeader>
      <ModalBody>
        <Cropper
          image={image.url}
          aspect={GALLERY_RATIO_VALUE[aspectRatio]}
          crop={position}
          zoom={zoom}
          minZoom={MIN_ZOOM}
          maxZoom={maxZoom}
          onCropChange={setPosition}
          onZoomChange={setZoom}
          onMediaLoaded={setMedia}
          onCropAreaChange={setArea}
          initialCroppedAreaPercentages={initialArea}
          style={CROPPER_STYLE}
        />
        <input
          type="range"
          className="custom-range mt-3"
          min={MIN_ZOOM}
          max={maxZoom}
          step={0.01}
          value={zoom}
          aria-label="Zoom"
          onChange={(event) => setZoom(Number(event.target.value))}
        />
        <p className="text-muted small mb-0">
          Drag to move, use the slider or the wheel to zoom. The mask has the
          shape of the gallery aspect ratio ({aspectRatio}).
        </p>
        {lowRes && (
          <Alert color="warning" className="mb-0 mt-2 py-2 font-size-12">
            {lowResMessage(zoom > MIN_ZOOM)}
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        {image.crop && (
          <Button color="link" className="text-danger mr-auto" onClick={onRemove}>
            Remove crop
          </Button>
        )}
        <Button color="light" onClick={onClose}>
          Cancel
        </Button>
        <Button color="primary" disabled={!value} onClick={() => onSave(value)}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default GalleryCropModal;
